import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CountryList from 'country-list-with-dial-code-and-flag';
import { supabase } from '../../lib/supabase';
import { emailService } from '../../services/emailService';

const COUNTRY_CODES = CountryList.getAll({ withSecondary: false });

const RegistrationScreen = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    countryCode: 'IN',
    mobile: '',
    agreeTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const selectedCountry = COUNTRY_CODES.find((country) => country.code === formData.countryCode);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const startTimer = () => {
    setOtpTimer(60);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'fullName':
        if (!value?.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (value.trim().length > 100) return 'Name must be under 100 characters';
        return '';
      case 'email':
        if (!value?.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address';
        return '';
      case 'mobile':
        if (!value?.trim()) return 'Mobile number is required';
        if (!/^\d{6,15}$/.test(value.trim())) return 'Enter a valid mobile number';
        return '';
      case 'agreeTerms':
        if (!value) return 'You must agree to the terms to continue';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const fields = ['fullName', 'email', 'mobile', 'agreeTerms'];
    const newErrors = {};
    const newTouched = {};
    fields.forEach((field) => {
      newTouched[field] = true;
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    });
    if (!emailVerified) newErrors.emailVerify = 'Please verify your email address';
    setErrors(newErrors);
    setTouched(newTouched);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = name === 'mobile'
      ? value.replace(/\D/g, '').slice(0, 15)
      : type === 'checkbox'
        ? checked
        : value;
    setFormData((prev) => ({ ...prev, [name]: inputValue }));
    if (successMessage) setSuccessMessage('');
    if (name === 'email') {
      setEmailVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpError('');
    }
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, inputValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSendOtp = async () => {
    const emailErr = validateField('email', formData.email);
    if (emailErr) {
      console.log(emailErr);
      setErrors((prev) => ({ ...prev, email: emailErr }));
      setTouched((prev) => ({ ...prev, email: true }));
      return;
    }
    setOtpLoading(true);
    setOtpError('');

    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const expiry = new Date(Date.now() + 10 * 60000); // 10 minutes
    try {
      const { error } = await supabase.from('otp_verifications')
      .insert([{ email: formData.email, otp_code: otp, expires_at: expiry }]);
      // const { error } = await supabase.auth.signInWithOtp({
      //   email: formData.email.trim().toLowerCase(),
      //   options: { shouldCreateUser: false },
      // });
      
        console.log(error);

      if (error) {
        console.log(error);
        if (error.status === 429) {
          setOtpError('Too many requests. Please wait and try again.');
        } else if (error.message?.includes('User not found')) {
          setOtpError('User does not exist. Please sign up first.');
        } else {
          setOtpError(error.message || 'Failed to send OTP.');
        }
        return;
      }

      await emailService.sendverifyOtpEmail(formData.email, otp, expiry);

      setOtpSent(true);
      startTimer();

    } catch (err) {
      setOtpError('Failed to send OTP. Please try again.');
      console.log(err);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError('Please enter the OTP sent to your email');
      return;
    }

    setVerifyLoading(true);const handleSendOtp = async () => {
      const emailVal = formData.email.trim().toLowerCase();
      const emailErr = validateField('email', emailVal);
      
      if (emailErr) {
        setErrors((prev) => ({ ...prev, email: emailErr }));
        setTouched((prev) => ({ ...prev, email: true }));
        return;
      }

      setOtpLoading(true);
      setOtpError('');

      // Generate 6-digit OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const expiry = new Date(Date.now() + 10 * 60000).toISOString(); // 10 minutes from now

      try {
        // 1. Store OTP in your custom table
        // Note: We use .insert(). If you want to prevent spam, 
        // you could check for existing valid OTPs here first.
        const { error } = await supabase
          .from('otp_verifications')
          .insert([
            { 
              email: emailVal, 
              otp_code: otp, 
              expires_at: expiry,
              is_used: false 
            }
          ]);

        if (error) {
          setOtpError(error.message || 'Database error occurred.');
          return;
        }

        // 2. Send the actual email via your service
        // Pass the expiry so the email can tell the user "This expires in 10 mins"
        await emailService.sendverifyOtpEmail(emailVal, otp, expiry);

        setOtpSent(true);
        startTimer();

      } catch (err) {
        console.error('OTP Error:', err);
        setOtpError('Failed to send OTP. Please try again.');
      } finally {
        setOtpLoading(false);
      }
    };
    setOtpError('');

    try {
      const userEmail = formData.email.trim().toLowerCase();

      // 1. Query your custom mapping table
      const { data, error } = await supabase
        .from('otp_verifications')
        .select('*')
        .eq('email', userEmail)
        .eq('otp_code', otp) // This matches the user input
        .eq('is_used', false)
        .gt('expires_at', new Date().toISOString()) // Check if not expired
        .maybeSingle(); // Returns null instead of error if not found

      if (error || !data) {
        setOtpError('Invalid or expired OTP. Please try again.');
        return;
      }

      // 2. Mark the OTP as used so it can't be reused
      await supabase
        .from('otp_verifications')
        .update({ is_used: true })
        .eq('id', data.id);

      // 3. Success state updates
      setVerifiedEmail(userEmail);
      setEmailVerified(true);
      setOtpSent(false);
      setErrors((prev) => ({ ...prev, emailVerify: '' }));

    } catch (err) {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const generateRandomPassword = (length = 16) => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let retVal = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Basic validation for Full Name, Mobile, and Password
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const email = verifiedEmail;
      const fullName = formData.fullName.trim();
      const dialCode = selectedCountry?.dialCode || '';
      const mobileNumber = `${dialCode} ${formData.mobile.trim()}`.trim();

      // Generate a random password since the user verified via OTP
      const temporaryPassword = generateRandomPassword();

      // 1. Create the Auth User 
      // This triggers your Postgres function to sync data to public.user_profiles
      const { data, error: authError } = await supabase.auth.signUp({
        email: email,
        password: temporaryPassword,
        options: {
          data: {
            full_name: fullName,
            mobile: mobileNumber,
          }
        }
      });

      if (authError) throw authError;

      // 2. Send the Welcome Email
      // Using your emailService since verification is already done
      await emailService.sendWelcomeEmail(email, fullName);

      // 3. Clear session and redirect
      // signUp often logs the user in automatically; we sign out to force a fresh login
      await supabase.auth.signOut();

      await emailService.sendAdminNotification({
        name: fullName,
        email: email,
        type: "Direct Register"
      });
      
      setSuccessMessage('Account created! Please log in with your credentials.');
      
      setTimeout(() => {
        navigate('/login-screen');
      }, 1500);

    } catch (err) {
      console.error('Registration Error:', err);
      setErrors({ submit: err?.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Regular'), local('Sansation'), local('Sansation-Regular');
          font-weight: 400;
          font-style: normal;
        }
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Bold'), local('Sansation-Bold');
          font-weight: 700;
          font-style: normal;
        }
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Light'), local('Sansation-Light');
          font-weight: 300;
          font-style: normal;
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── Headings use Sansation; everything else inherits ── */
        .reg-root h1, .reg-root h2, .reg-root h3 {
          font-family: 'Sansation', sans-serif;
        }

        /* ── Root layout ─────────────────────────────────────── */
        .reg-root {
          min-height: 100dvh;
          width: 100%;
          background-image: url('/assets/images/Join 1000+ Founders Login.svg');
          background-size: cover;
          background-position: top left;
          background-repeat: no-repeat;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 24px 5% 24px 0;
          font-family: inherit;
        }

        /* ── Card ────────────────────────────────────────────── */
        .reg-card {
          width: min(460px, 100%);
          background: rgba(255, 255, 255, 0.97);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 32px 44px 28px;
          box-shadow: 0 32px 80px rgba(0,0,0,0.22), 0 8px 24px rgba(0,0,0,0.10);
          border: 1px solid rgba(255, 255, 255, 0.6);
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        /* ── Mobile hero image (hidden on desktop) ───────────── */
        .reg-mobile-hero {
          display: none;
        }

        /* ── Inputs ──────────────────────────────────────────── */
        .reg-input {
          width: 100%;
          padding: 11px 14px;
          border: 1.5px solid #E5E7EB;
          border-radius: 10px;
          font-size: 14px;
          font-family: inherit;
          color: #0F172A;
          background: #F9FAFB;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
          -webkit-text-size-adjust: 100%;
        }
        .reg-input:focus {
          border-color: #3B6BE4;
          box-shadow: 0 0 0 3px rgba(59,107,228,0.10);
          background: #fff;
        }
        .reg-input.has-error { border-color: #EF4444; }
        .reg-input.has-error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.10); }
        .reg-input:disabled { background: #F3F4F6; color: #9CA3AF; cursor: not-allowed; }
        .reg-input.verified { border-color: #16A34A; background: #F0FDF4; }

        /* ── Primary button ──────────────────────────────────── */
        .reg-btn {
          width: 100%;
          padding: 13px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
          color: #fff;
          font-weight: 600;
          font-size: 15px;
          font-family: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 16px rgba(59,107,228,0.40);
          cursor: pointer;
          transition: opacity 0.2s, transform 0.15s;
          margin-top: 6px;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        .reg-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
        .reg-btn:active:not(:disabled) { opacity: 0.85; transform: translateY(0); }
        .reg-btn:disabled { background: #93AEED; cursor: not-allowed; box-shadow: none; }

        /* ── Verify / OTP buttons ────────────────────────────── */
        .verify-btn {
          padding: 10px 14px;
          border-radius: 8px;
          border: none;
          background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
          color: #fff;
          font-weight: 600;
          font-size: 13px;
          font-family: inherit;
          white-space: nowrap;
          cursor: pointer;
          transition: opacity 0.2s;
          min-width: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          flex-shrink: 0;
        }
        .verify-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .verify-btn.verified-state { cursor: default; }

        /* ── OTP row ─────────────────────────────────────────── */
        .otp-input-row {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }

        /* ── Typography helpers ──────────────────────────────── */
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        .field-error {
          font-size: 12px;
          color: #DC2626;
          margin-top: 5px;
          margin-bottom: 0;
        }

        /* ── Animations ──────────────────────────────────────── */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in { animation: fadeIn 0.25s ease; }

        @keyframes slideDown {
          from { opacity: 0; max-height: 0; }
          to   { opacity: 1; max-height: 120px; }
        }
        .slide-down { animation: slideDown 0.25s ease; overflow: hidden; }

        /* ── Checkbox ────────────────────────────────────────── */
        .check-wrap {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }
        .check-wrap input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          min-width: 18px;
          border: 2px solid #D1D5DB;
          border-radius: 5px;
          background: #F9FAFB;
          cursor: pointer;
          transition: all 0.15s;
          position: relative;
          top: 1px;
          flex-shrink: 0;
        }
        .check-wrap input[type="checkbox"]:checked {
          background: #3B6BE4;
          border-color: #3B6BE4;
          background-image: url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: center;
        }
        .check-wrap input[type="checkbox"]:disabled { opacity: 0.5; cursor: not-allowed; }
        .check-wrap input[type="checkbox"]:focus { outline: none; box-shadow: 0 0 0 3px rgba(59,107,228,0.15); }

        /* ── Mobile prefix ───────────────────────────────────── */
        .mobile-prefix {
          display: flex;
          align-items: center;
          position: relative;
          min-width: 78px;
          justify-content: center;
          padding: 0 26px 0 12px;
          background: #F3F4F6;
          border: 1.5px solid #E5E7EB;
          border-right: none;
          border-radius: 10px 0 0 10px;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .mobile-prefix-select {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
        .mobile-prefix-select:disabled {
          cursor: not-allowed;
        }
        .mobile-prefix-value {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: inherit;
          font: inherit;
          font-weight: 600;
          pointer-events: none;
        }
        .mobile-input {
          border-radius: 0 10px 10px 0 !important;
        }

        /* ── Form gap ────────────────────────────────────────── */
        .reg-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* ══════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
           ══════════════════════════════════════════════════════ */

        /* ── Large tablets / small laptops (≤ 900px) ─────────── */
        @media (max-width: 900px) {
          .reg-root {
            justify-content: center;
            padding: 24px 16px;
          }
          .reg-card {
            width: min(480px, 100%);
          }
        }

        /* ── Tablets (601px–768px) ───────────────────────────── */
        @media (min-width: 601px) and (max-width: 768px) {
          .reg-root {
            align-items: flex-start;
            padding: 16px;
          }
          .reg-card {
            width: 100%;
            max-width: 480px;
            margin: 0 auto;
            padding: 28px 32px 24px;
          }
        }

        /* ── Mobile (≤ 600px) ────────────────────────────────── */
        @media (max-width: 600px) {
          .reg-root {
            background-image: none;
            background-color: #f3f4f6;
            align-items: stretch;
            justify-content: flex-start;
            padding: 0;
            flex-direction: column;
            overflow-y: auto;
            min-height: 100dvh;
            width: 100%;
          }

          .reg-mobile-hero {
            display: block;
            width: 100%;
            height: min(180px, 35vh);
            background-image: url('/assets/images/Join 1000+ Founders Login.svg');
            background-size: cover;
            background-position: center 20%;
            background-repeat: no-repeat;
            flex-shrink: 0;
          }

          .reg-card {
            width: 100% !important;
            max-width: 100% !important;
            margin: -16px 0 0 0 !important;
            border-radius: 16px 16px 0 0;
            padding: 28px 20px 32px;
            padding-bottom: max(32px, env(safe-area-inset-bottom));
            box-shadow: 0 -4px 20px rgba(0,0,0,0.10);
            border: none;
            background: #fff;
            flex: 1;
            overflow-y: visible;
            position: relative;
            z-index: 1;
          }

          .reg-input {
            font-size: 16px;
          }

          .verify-btn {
            min-width: 72px;
            padding: 10px 10px;
            font-size: 13px;
          }
          .mobile-prefix {
            font-size: 13px;
            min-width: 74px;
            padding: 0 24px 0 10px;
          }
        }

        /* ── Very small phones (≤ 375px) ─────────────────────── */
        @media (max-width: 375px) {
          .reg-mobile-hero {
            height: min(160px, 30vh);
          }
          .reg-card {
            padding: 24px 16px 28px !important;
            padding-bottom: max(28px, env(safe-area-inset-bottom)) !important;
          }
          .reg-btn {
            font-size: 14px;
            padding: 12px;
          }
          .verify-btn {
            min-width: 64px;
            padding: 9px 8px;
            font-size: 12px;
          }
          .form-label {
            font-size: 12px;
          }
          .mobile-prefix {
            font-size: 12px;
            min-width: 70px;
            padding: 0 22px 0 8px;
          }
        }
      `}</style>

      <div className="reg-root">
        {/* Mobile-only hero image strip */}
        <div className="reg-mobile-hero" aria-hidden="true" />

        <div className="reg-card">

          {/* ── Logo ── */}
          <div style={{ marginBottom: '14px' }}>
            <img
              src="/assets/images/8.svg"
              alt="DecX AI"
              style={{ height: '36px', width: 'auto', objectFit: 'contain', display: 'block', marginBottom: '8px' }}
            />
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
              Join <strong style={{ color: '#111827' }}>DecX AI</strong>
            </p>
            <h1 style={{ fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: '700', color: '#0F172A', lineHeight: '1.3', margin: '4px 0 0' }}>
              Create your account
            </h1>
          </div>

          {/* ── Success Banner ── */}
          {successMessage && (
            <div className="fade-in" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '11px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <p style={{ fontSize: '13px', color: '#15803D', margin: 0 }}>{successMessage}</p>
            </div>
          )}

          {/* ── Error Banner ── */}
          {errors?.submit && (
            <div className="fade-in" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '11px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{errors.submit}</p>
            </div>
          )}

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} noValidate className="reg-form">

            {/* Full Name */}
            <div>
              <label className="form-label">Full Name <span style={{ color: '#EF4444' }}>*</span></label>
              <input
                className={`reg-input${touched.fullName && errors.fullName ? ' has-error' : ''}`}
                type="text"
                name="fullName"
                placeholder="Jane Smith"
                value={formData.fullName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoading}
                autoComplete="name"
              />
              {touched.fullName && errors.fullName && <p className="field-error">{errors.fullName}</p>}
            </div>

            {/* Email + Verify */}
            <div>
              <label className="form-label">Email Address <span style={{ color: '#EF4444' }}>*</span></label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className={`reg-input${touched.email && errors.email ? ' has-error' : ''}${emailVerified ? ' verified' : ''}`}
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isLoading || emailVerified}
                  autoComplete="email"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  className={`verify-btn${emailVerified ? ' verified-state' : ''}`}
                  onClick={emailVerified ? undefined : handleSendOtp}
                  disabled={otpLoading || isLoading || (otpSent && otpTimer > 0)}
                >
                  {emailVerified ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : otpLoading ? (
                    <svg className="spin" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/><path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8v8z"/></svg>
                  ) : otpSent && otpTimer > 0 ? (
                    `${otpTimer}s`
                  ) : otpSent ? (
                    'Resend'
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
              {touched.email && errors.email && <p className="field-error">{errors.email}</p>}
              {errors.emailVerify && !emailVerified && <p className="field-error">{errors.emailVerify}</p>}

              {/* OTP Input Row */}
              {otpSent && !emailVerified && (
                <div className="slide-down">
                  <div className="otp-input-row">
                    <input
                      className={`reg-input${otpError ? ' has-error' : ''}`}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter OTP from email"
                      value={otp}
                      onChange={(e) => { setOtp(e.target.value); setOtpError(''); }}
                      maxLength={6}
                      style={{ flex: 1, minWidth: 0, letterSpacing: '3px', fontWeight: '600' }}
                      disabled={verifyLoading || isLoading}
                    />
                    <button
                      type="button"
                      className="verify-btn"
                      onClick={handleVerifyOtp}
                      disabled={verifyLoading || isLoading || !otp}
                    >
                      {verifyLoading ? (
                        <svg className="spin" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/><path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8v8z"/></svg>
                      ) : 'Submit'}
                    </button>
                  </div>
                  {otpError && <p className="field-error">{otpError}</p>}
                  <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '5px', marginBottom: 0 }}>
                    Check your inbox for the OTP code
                  </p>
                </div>
              )}

              {/* Verified badge */}
              {emailVerified && (
                <p className="fade-in" style={{ fontSize: '12px', color: '#16A34A', marginTop: '5px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Email verified successfully
                </p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="form-label">
                Mobile Number <span style={{ color: '#EF4444' }}>*</span>
                <span style={{ fontWeight: '400', color: '#9CA3AF', fontSize: '11px', marginLeft: '6px' }}>for account only</span>
              </label>
              <div style={{ display: 'flex' }}>
                <div className="mobile-prefix">
                  <select
                    className="mobile-prefix-select"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    aria-label="Select country code"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={`${country.code}-${country.dialCode}`} value={country.code}>
                        {country.name} ({country.dialCode})
                      </option>
                    ))}
                  </select>
                  <span className="mobile-prefix-value">
                    <span>{selectedCountry?.flag || ''}</span>
                    <span>{selectedCountry?.dialCode || ''}</span>
                  </span>
                </div>
                <input
                  className={`reg-input mobile-input${touched.mobile && errors.mobile ? ' has-error' : ''}`}
                  type="tel"
                  inputMode="numeric"
                  name="mobile"
                  placeholder="Enter mobile number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  autoComplete="tel"
                  maxLength={15}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
              {touched.mobile && errors.mobile && <p className="field-error">{errors.mobile}</p>}
            </div>

            {/* Terms */}
            <div>
              <label className="check-wrap">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <span style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                  I agree to the{' '}
                  <a href="https://decx.ai/#/terms" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="https://decx.ai/#/privacy" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>Privacy Policy</a>
                </span>
              </label>
              {touched.agreeTerms && errors.agreeTerms && (
                <p className="field-error" style={{ marginLeft: '28px' }}>{errors.agreeTerms}</p>
              )}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading} className="reg-btn">
              {isLoading ? (
                <>
                  <svg className="spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                    <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Creating Account…
                </>
              ) : (
                <>
                  Create Account
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </>
              )}
            </button>
          </form>

          {/* ── Sign In Link ── */}
          <p style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280', marginTop: '12px', marginBottom: 0 }}>
            Already have an account?{' '}
            <Link to="/login-screen" style={{ color: '#3B6BE4', fontWeight: '600', textDecoration: 'none' }}>
              Sign In
            </Link>
          </p>

          {/* ── Footer ── */}
          <div style={{ borderTop: '1px solid #F3F4F6', marginTop: '12px', paddingTop: '10px' }}>
            <p style={{ textAlign: 'center', fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
              By continuing you agree to our{' '}
              <a href="https://decx.ai/#/privacy" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>privacy policy</a>
              {' '}and{' '}
              <a href="https://decx.ai/#/terms" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>terms of use</a>
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default RegistrationScreen;
